import { Box } from '@amzn/ring-ui-react-components';
import debounce from 'debounce-promise';
import { border } from 'polished';
import { forwardRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ControlProps,
  GroupBase,
  InputActionMeta,
  SingleValue,
} from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';
import {
  BaseOptionType,
  getSelectStyles,
  Option,
} from 'src/shared/components/Select';
import { useTheme } from 'src/shared/context';
import { useGetLocationAutocompleteOptions } from '../hooks';
import type { CSSObject } from '@emotion/serialize';
import type Select from 'react-select/dist/declarations/src/Select';

export type LocationOptionType = BaseOptionType<string>;

// TODO: for some reason react-select's default export is not generic, so we need to rebuild the component type for forwardRef
type LocationSelectType = Select<LocationOptionType, false>;

export type LocationSelectProps = AsyncProps<
  LocationOptionType,
  false,
  GroupBase<LocationOptionType>
> &
  UseSelectStylesProps & {
    onChange: (...event: any[]) => void;
  };

export const LocationSelect = forwardRef<
  LocationSelectType,
  LocationSelectProps
>(({ isInvalid, onChange, defaultInputValue, defaultValue, ...props }, ref) => {
  const { t } = useTranslation('components.posting');
  const getOptions = useGetLocationAutocompleteOptions();

  const styles = useSelectStyles({
    isInvalid,
  });

  const loadOptions = debounce(async (address: string) => {
    if (address.trim().length < MIN_CHARACTER_COUNT) {
      return [];
    }

    const {
      data: { locationAutoCompleteByPinPreference },
      errors,
    } = await getOptions(address);

    if (typeof errors !== 'undefined' && errors.length > 0) {
      return [];
    }

    const { addresses } = locationAutoCompleteByPinPreference!;

    return addresses.map((address) => ({
      label: address,
      value: address,
    }));
  }, 500);

  const [value, setValue] = useState<SingleValue<LocationOptionType>>();
  const [inputValue, setInputValue] = useState(defaultInputValue);

  const handleLocationInputChange = (
    newInputValue: string,
    actionMeta: InputActionMeta,
  ) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(newInputValue);
    }
  };

  const handleLocationChange = (option: SingleValue<LocationOptionType>) => {
    setValue(option);
    setInputValue(option ? option.value : '');
    onChange({ target: { value: option } });
  };

  return (
    <Box width="100%" position="relative" data-testid="location-select">
      <AsyncSelect
        {...props}
        components={components}
        isClearable
        loadOptions={loadOptions}
        openMenuOnClick={false}
        placeholder={t('location-select.placeholder')}
        ref={ref}
        styles={styles}
        value={value}
        inputValue={inputValue}
        onChange={handleLocationChange}
        onInputChange={handleLocationInputChange}
        defaultValue={defaultValue}
      />
    </Box>
  );
});

const MIN_CHARACTER_COUNT = 3;

const components = {
  Option,
};

interface UseSelectStylesProps {
  isInvalid?: boolean;
}

const useSelectStyles = ({ isInvalid }: UseSelectStylesProps) => {
  const theme = useTheme();

  return useMemo(() => {
    const styles = getSelectStyles(theme);

    return {
      ...styles,
      control: (base: CSSObject, props: ControlProps<LocationOptionType>) => ({
        ...styles.control(base, props),
        ...(isInvalid && {
          ...border('1px', 'solid', theme.colors.negative[500]),
          boxShadow: `0 0 0 1px ${theme.colors.negative[500]}`,
          '&:hover': {
            borderColor: theme.colors.negative[500],
          },
        }),
      }),
      input: (base: CSSObject) => ({
        ...styles.input(base),
        input: {
          opacity: '1 !important',
        },
      }),
    };
  }, [isInvalid, theme]);
};
